/**
 * 改良版トランスクリプトパネル
 * トランスクリプトの表示と処理を担当
 */

import React, { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangleIcon, 
  CheckCircleIcon, 
  InfoIcon, 
  Loader2Icon, 
  MessageSquareIcon, 
  PlayIcon, 
  SettingsIcon
} from 'lucide-react';
import { useMapState, useMapExtraction, useMapUI } from '@/contexts/map';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';

/**
 * トランスクリプトパネルの設定オプション
 */
interface TranscriptOptions {
  autoExtract: boolean;
  minimumLength: number;
  showHighlights: boolean;
}

/**
 * トランスクリプトパネルコンポーネント
 */
const TranscriptPanel: React.FC = () => {
  const { transcript, setTranscript, isProcessing, setIsProcessing } = useMapUI();
  const { mapData, updateNode, updateEdge, updateTranscript } = useMapState();
  const { processAll, extracting, progress } = useMapExtraction();
  const [activeTab, setActiveTab] = useState<string>('edit');
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [options, setOptions] = useState<TranscriptOptions>({
    autoExtract: false,
    minimumLength: 200,
    showHighlights: true
  });
  
  // サンプルトランスクリプトを使用
  React.useEffect(() => {
    if (!transcript && mapData && mapData.transcript) {
      setTranscript(mapData.transcript);
    }
  }, [mapData, transcript, setTranscript]);
  
  // トランスクリプト変更ハンドラー
  const handleTranscriptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTranscript(e.target.value);
    
    // 自動抽出オプションが有効で、最小長さを超えた場合
    if (options.autoExtract && 
        e.target.value.length >= options.minimumLength && 
        !isProcessing && 
        !extracting) {
      // 自動抽出のトースト通知
      toast.info('自動抽出が開始されます', {
        description: '設定で無効にすることもできます',
        duration: 3000,
      });
      
      // 少し遅延させて処理開始
      setTimeout(() => {
        handleProcess();
      }, 1000);
    }
  }, [setTranscript, options, isProcessing, extracting]);
  
  // 処理実行ハンドラー
  const handleProcess = useCallback(async () => {
    if (!mapData || !transcript) return;
    
    setIsProcessing(true);
    
    try {
      // トランスクリプト更新
      updateTranscript(transcript);
      
      // すべてのノードとエッジを処理
      const result = await processAll(mapData.nodes, mapData.edges, transcript);
      
      // ノードとエッジの更新
      result.nodes.forEach(node => {
        updateNode(node.id, node);
      });
      
      result.edges.forEach(edge => {
        updateEdge(edge.id, edge);
      });
      
      // 処理完了時のトースト
      toast.success('処理完了', {
        description: 'トピックの抽出が完了しました。',
        duration: 3000,
      });
      
      // 成功したら表示タブに切り替え
      setActiveTab('view');
    } catch (error) {
      toast.error('エラー', {
        description: '処理中にエラーが発生しました。',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [mapData, transcript, updateTranscript, processAll, updateNode, updateEdge, setIsProcessing]);
  
  // トランスクリプトの文字数
  const transcriptLength = transcript?.length || 0;
  
  // 処理ボタンの無効化条件
  const isProcessButtonDisabled = !transcript || isProcessing || extracting || transcriptLength < 10;
  
  // オプション変更ハンドラー
  const handleOptionChange = useCallback((key: keyof TranscriptOptions, value: any) => {
    setOptions(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  return (
    <Card className="shadow-none border-0 h-full flex flex-col">
      <CardHeader className="px-4 py-3 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center">
            <MessageSquareIcon className="h-4 w-4 mr-2" />
            会話トランスクリプト
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={transcriptLength > 0 ? "outline" : "secondary"} className="h-6">
              {transcriptLength} 文字
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden p-4">
        <TabsList className="mb-0 mt-0">
          <TabsTrigger value="edit">編集</TabsTrigger>
          <TabsTrigger value="view">表示</TabsTrigger>
        </TabsList>
        
        <CardContent className="p-0 flex-1 overflow-hidden">
          <TabsContent value="edit" className="h-full m-0 p-0 data-[state=active]:flex data-[state=active]:flex-col">
            <div className="p-4 pb-0">
              <Alert className="mb-3">
                <InfoIcon className="h-4 w-4" />
                <AlertTitle>トランスクリプトの編集</AlertTitle>
                <AlertDescription className="text-xs">
                  会話の内容を入力してから「トピックを抽出」ボタンをクリックして分析を開始してください。
                </AlertDescription>
              </Alert>
            </div>
            
            <div className="px-4 py-2 flex-1 overflow-hidden">
              <div className="h-full flex flex-col">
                <Textarea
                  placeholder="会話内容を入力してください..."
                  className="flex-1 min-h-[200px] text-sm resize-none"
                  value={transcript}
                  onChange={handleTranscriptChange}
                  disabled={isProcessing || extracting}
                />
              </div>
            </div>
            
            <div className="p-4 pt-3 border-t">
              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleProcess}
                  disabled={isProcessButtonDisabled}
                  className="w-full gap-2"
                >
                  {isProcessing || extracting ? (
                    <>
                      <Loader2Icon className="h-4 w-4 animate-spin" />
                      処理中...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-4 w-4" />
                      トピックを抽出
                    </>
                  )}
                </Button>
                
                {(isProcessing || extracting) && (
                  <div>
                    <div className="flex justify-between items-center text-xs text-muted-foreground mb-1.5">
                      <span>処理中...</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-1.5" />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="view" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
            <div className="p-4 flex-1 overflow-hidden">
              <ScrollArea className="h-full pr-4">
                {!transcript ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <AlertTriangleIcon className="h-8 w-8 mb-2 mx-auto opacity-50" />
                      <p className="text-sm">トランスクリプトがありません</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transcript.split('\n\n').map((paragraph, index) => (
                      <div key={index} className="space-y-1">
                        {paragraph.split('\n').map((line, lineIndex) => (
                          <p key={`${index}-${lineIndex}`} className="text-sm">
                            {line}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
            
            <CardFooter className="px-4 py-3 border-t flex justify-between">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setActiveTab('edit')}
                className="h-8"
              >
                編集に戻る
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (options.showHighlights) {
                    toast.info('ハイライト機能は現在開発中です');
                  }
                }}
                className="h-8 gap-1.5"
                disabled={!options.showHighlights}
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
                キーワードをハイライト
              </Button>
            </CardFooter>
          </TabsContent>
        </CardContent>
      </Tabs>
      
      <CardFooter className="px-4 py-3 border-t">
        <div className="w-full flex justify-between items-center">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2"
            onClick={() => setIsOptionsOpen(true)}
          >
            <SettingsIcon className="h-4 w-4 mr-1.5" />
            オプション
          </Button>
          
          <div className="flex items-center text-xs text-muted-foreground">
            {mapData?.nodes.filter(n => n.active).length || 0} 個のトピックが抽出されています
          </div>
        </div>
      </CardFooter>
      
      {/* オプションダイアログ (モーダルで実装) */}
      <Dialog open={isOptionsOpen} onOpenChange={setIsOptionsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>トランスクリプト設定</DialogTitle>
            <DialogDescription>
              分析オプションをカスタマイズできます
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-extract">自動抽出</Label>
                <p className="text-xs text-muted-foreground">
                  テキスト入力後に自動的に分析を開始します
                </p>
              </div>
              <Switch 
                id="auto-extract" 
                checked={options.autoExtract}
                onCheckedChange={(checked) => handleOptionChange('autoExtract', checked)}
              />
            </div>
            
            <div className="flex flex-col space-y-2">
              <Label htmlFor="min-length">抽出最小文字数</Label>
              <Input 
                id="min-length"
                type="number" 
                value={options.minimumLength}
                onChange={(e) => handleOptionChange('minimumLength', parseInt(e.target.value) || 0)}
                min={50}
                max={1000}
              />
              <p className="text-xs text-muted-foreground">
                自動抽出を開始する最小文字数（50-1000）
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-highlights">キーワードハイライト</Label>
                <p className="text-xs text-muted-foreground">
                  重要なキーワードを強調表示します
                </p>
              </div>
              <Switch 
                id="show-highlights" 
                checked={options.showHighlights}
                onCheckedChange={(checked) => handleOptionChange('showHighlights', checked)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button 
                onClick={() => {
                  toast.success('設定を保存しました', {
                    description: '変更が適用されました',
                    duration: 3000,
                  });
                }}
              >
                保存
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TranscriptPanel;